/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { useFlags } from 'sarsaparilla';

import { AvailableNearby } from './AvailableNearby';
import { NearWater } from './NearWater';
import { HolidayStays } from './HolidayStays';
import { HiddenGems } from './HiddenGems';
import { ActivitiesCarousel } from './ActivitiesCarousel';

interface RecommendationCarouselsProps {
    position?: 'upper' | 'lower';
}

const carouselsById: Record<string, React.FC> = {
    availableNearby: AvailableNearby,
    nearWater: NearWater,
    holidayStays: HolidayStays,
    hiddenGems: HiddenGems,
    activities: ActivitiesCarousel,
};

/**
 * Uses comma-separated list of carousel IDs from LaunchDarkly to determine which
 * carousels to render.
 *
 * The LaunchDarkly values must be a comma-separated list of carousel IDs, and are required
 * for any content to be displayed. If LaunchDarkly is not available no carousels will be shown
 *
 * Note that this components does not provide any spacing between the carousels because
 * the homepageContainer renders this inside a Stack, and if the API call made by a carousel
 * fails, any wrapping element would be empty but still cause an extra space to be added
 * in the HomepageContainer Stack
 */
export function RecommendationCarousels({ position }: RecommendationCarouselsProps) {
    const flags = useFlags();

    const flagName =
        position === 'upper'
            ? 'homepageRecommendationsUpper'
            : 'homepageRecommendationsLower';

    // The Launch Darkly flag value is expected to be a comma-separated list of carousel IDs
    // e.g., "availableNearby,nearWater"
    const flagValue: unknown = flags[flagName] ?? null;

    // Bail if there is no flag value or if it's not a string
    if (!flagValue || typeof flagValue !== 'string') {
        return null;
    }

    const ids = flagValue.split(',').map((promoId) => promoId.trim());

    const carouselsToRender = ids
        .map((id) => {
            const PromoComponent = carouselsById[id];
            return PromoComponent ? <PromoComponent key={id} /> : null;
        })
        .filter(Boolean);

    if (carouselsToRender.length === 0) {
        return null;
    }

    return carouselsToRender;
}
